// Framework-agnostic Zendesk Messaging Web Widget helper.
// Based on the Lautec zendesk-integration helper (angular.md / messenger.ts),
// extended with custom-launcher controls (open / close / event subscriptions)
// so the widget can be driven from our own sidebar button instead of the
// default floating launcher.

/** A value that can be written to a Zendesk custom ticket field via the metadata API. */
type ZendeskFieldValue = string | number | boolean;

interface ZendeskConversationField {
  /**
   * The custom ticket field ID from Zendesk Admin Center
   * (Objects and rules → Tickets → Fields). Must be a string.
   * The field must have the "Customers can edit" permission, otherwise
   * the value is silently ignored by Zendesk.
   */
  id: string;
  value: ZendeskFieldValue;
}

/** The messaging Web Widget command queue exposed globally once snippet.js runs. */
type ZendeskCommandQueue = (...args: unknown[]) => void;

declare global {
  interface Window {
    zE?: ZendeskCommandQueue;
  }
}

/** Returns a freshly-signed Zendesk JWT for the current user (from the BFF), or null if unavailable. */
export type ZendeskTokenProvider = () => Promise<string | null>;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const READY_RETRY_MS = 250;
const READY_MAX_ATTEMPTS = 40; // ~10s total

/**
 * Defers execution until window.zE exists (the snippet loads asynchronously).
 * Retries for up to ~10 s, then gives up silently — support is non-critical.
 *
 * Resolves `true` once the command has been dispatched to the widget, or `false`
 * if it gave up (no window / snippet never became ready) — so a run-once caller
 * can retry later instead of latching on a dispatch that never happened.
 */
const runWhenReady = async (
  run: (zE: NonNullable<Window['zE']>) => void
): Promise<boolean> => {
  if (typeof window === 'undefined') return false;

  for (let attempt = 0; attempt <= READY_MAX_ATTEMPTS; attempt++) {
    const zE = window.zE;
    if (typeof zE === 'function') {
      try {
        run(zE);
      } catch {
        // Widget present but the command failed — ignore; support is optional.
      }
      return true;
    }
    if (attempt < READY_MAX_ATTEMPTS) {
      await new Promise((resolve) =>
        window.setTimeout(resolve, READY_RETRY_MS)
      );
    }
  }
  return false;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** True once the messaging widget's command queue is available. */
export const isZendeskAvailable = (): boolean =>
  typeof window !== 'undefined' && typeof window.zE === 'function';

/**
 * Attach custom ticket field values to the visitor's current messaging conversation.
 * Empty/blank values are dropped so we never overwrite a field with nothing.
 */
export const setZendeskConversationFields = (
  fields: ZendeskConversationField[]
): void => {
  const valid = fields.filter(
    (f) =>
      Boolean(f.id) &&
      f.value !== '' &&
      f.value !== null &&
      f.value !== undefined
  );
  if (valid.length === 0) return;
  runWhenReady((zE) => zE('messenger:set', 'conversationFields', valid));
};

/** Attach tags to the visitor's current messaging conversation (useful for routing/filtering). */
export const setZendeskConversationTags = (tags: string[]): void => {
  const valid = tags.filter(Boolean);
  if (valid.length === 0) return;
  runWhenReady((zE) => zE('messenger:set', 'conversationTags', valid));
};

/**
 * Authenticate the messaging widget for the current user (SSO).
 *
 * Zendesk calls `getToken` immediately and again whenever the token expires or a
 * request returns 401, so it MUST fetch a fresh JWT each time. If `getToken`
 * resolves to null/empty or throws — e.g. the user isn't authenticated and the
 * backend returns 401/403 — the widget silently stays anonymous (the documented
 * fallback). Only call this on surfaces where the user is authenticated.
 */
export const loginZendeskUser = (
  getToken: ZendeskTokenProvider
): Promise<boolean> =>
  runWhenReady((zE) => {
    zE('messenger', 'loginUser', (callback: (jwt: string) => void) => {
      getToken()
        .then((token) => {
          if (token) callback(token);
        })
        .catch(() => {
          // Not authenticated / fetch failed — remain anonymous.
        });
    });
  });

/**
 * Clear the authenticated identity and its conversation/metadata. Call on user
 * sign-out so the next person on a shared machine doesn't inherit the session.
 */
export const logoutZendeskUser = (): void => {
  runWhenReady((zE) => zE('messenger', 'logoutUser'));
};

/** Show the messaging widget launcher. */
export const showZendeskWidget = (): void => {
  runWhenReady((zE) => zE('messenger', 'show'));
};

/** Hide the messaging widget launcher entirely (until shown again). */
export const hideZendeskWidget = (): void => {
  runWhenReady((zE) => zE('messenger', 'hide'));
};

// ---------------------------------------------------------------------------
// Custom launcher controls
//
// For a custom launcher, drive the messenger with `open`/`close` from your own
// button. Suppress the default floating launcher by hiding its iframe via CSS,
// NOT with the `hide` command: `hide` tears the widget down and `show` rebuilds
// it, adding a visible delay on every open/close. (`show`/`hide` are exported
// below for completeness, but the launcher is hidden via CSS to stay instant.)
// ---------------------------------------------------------------------------

/** Open the messaging window. */
export const openZendeskMessenger = (): void => {
  runWhenReady((zE) => zE('messenger', 'open'));
};

/** Close the messaging window. */
export const closeZendeskMessenger = (): void => {
  runWhenReady((zE) => zE('messenger', 'close'));
};

/** Subscribe to changes in the unread-message count (fires with the new count). */
export const onZendeskUnreadMessages = (
  callback: (count: number) => void
): Promise<boolean> =>
  runWhenReady((zE) => zE('messenger:on', 'unreadMessages', callback));

/** Subscribe to the messenger window opening. */
export const onZendeskMessengerOpen = (
  callback: () => void
): Promise<boolean> =>
  runWhenReady((zE) => zE('messenger:on', 'open', callback));

/** Subscribe to the messenger window closing. */
export const onZendeskMessengerClose = (
  callback: () => void
): Promise<boolean> =>
  runWhenReady((zE) => zE('messenger:on', 'close', callback));

/**
 * Builds a `prefix:id-name` conversation tag (e.g. "client:1-RebelDot"), sanitizing
 * the name to a tag-safe token (Zendesk tags can't contain spaces). Falls back to
 * id-only or name-only, and returns null when neither is usable.
 *
 * Portable: usable on web AND in mobile (native) tag construction.
 */
export const formatIdNameTag = (
  prefix: string,
  id?: number | string | null,
  name?: string | null
): string | null => {
  const safeName = name
    ? name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w/:-]/g, '')
    : '';
  const hasId =
    id !== null &&
    id !== undefined &&
    id !== '' &&
    !(typeof id === 'number' && Number.isNaN(id));

  if (hasId && safeName) return `${prefix}:${id}-${safeName}`;
  if (hasId) return `${prefix}:${id}`;
  if (safeName) return `${prefix}:${safeName}`;
  return null;
};
